import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Gift, Loader2, Search, UserPlus, X } from 'lucide-react';
import { cn } from '../poker-ui/utils/cn';
import { TableSeatProfileModal } from '../poker-ui/TableSeatProfileModal';
import { LobbyFiskiIcon, LobbyHubNotificationIndicator } from './lobbyHubShared';
import { acceptedFriendsFromRequests } from '../social/socialLobbyModel';
import './Lobby.css';

const TABS = [
  { id: 'friends', label: 'Братва' },
  { id: 'incoming', label: 'Входящие' },
  { id: 'recommended', label: 'Рекомендации' },
  { id: 'activity', label: 'Активность' },
];

const SOCIAL_STATE_KEY = 'seka-social-demo-v1';
const GIFT_AMOUNT = 100;
const CHALLENGE_MODE_OPTIONS = [
  { id: 'seka', label: 'Сека', sub: 'Классический режим' },
  { id: 'poker', label: 'Покер', sub: 'Texas Hold’em' },
  { id: 'belot', label: 'Белот', sub: 'Командный / дуэль' },
];
const CHALLENGE_STAKE_OPTIONS = [50, 100, 200, 500, 1000];

const PEOPLE_POOL = [
  {
    id: 'u-kiryuha',
    username: 'Kiryuha',
    level: 25,
    lastPlayedLabel: 'Последняя игра 4 ч назад',
    winningsLabel: '1.88M',
    headToHead: { type: 'winning', you: 5, them: 4 },
  },
  {
    id: 'u-igor',
    username: 'Igor',
    level: 79,
    lastPlayedLabel: 'Последняя игра 1 д назад',
    winningsLabel: '35.54M',
    headToHead: { type: 'winning', you: 37, them: 11 },
  },
  {
    id: 'u-luis',
    username: 'Luis Fabiano',
    level: 32,
    lastPlayedLabel: 'Последняя игра вчера',
    winningsLabel: '24.60M',
    headToHead: { type: 'none' },
  },
  {
    id: 'u-mila',
    username: 'Mila',
    level: 14,
    lastPlayedLabel: 'В сети',
    winningsLabel: '92K',
    headToHead: { type: 'losing', you: 3, them: 8 },
  },
  {
    id: 'u-nik',
    username: 'Nik',
    level: 47,
    lastPlayedLabel: 'Последняя игра 2 ч назад',
    winningsLabel: '8.2M',
    headToHead: { type: 'tie', you: 9, them: 9 },
  },
  {
    id: 'u-anna',
    username: 'Anna Pro',
    level: 56,
    lastPlayedLabel: 'Последняя игра 3 ч назад',
    winningsLabel: '4.1M',
    headToHead: { type: 'none' },
  },
];

const INITIAL_STATE = {
  incomingRequests: [
    {
      id: 'fr-in-1',
      from: PEOPLE_POOL[4],
      createdAt: Date.now() - 1000 * 60 * 45,
    },
  ],
  outgoingRequests: [],
  invites: [],
  gifts: [],
  coins: 1000,
};

function uid(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function headToHeadText(h2h) {
  if (!h2h || h2h.type === 'none') return 'Пока матчей нет';
  if (h2h.type === 'winning') return `Ты выигрываешь ${h2h.you} - ${h2h.them}`;
  if (h2h.type === 'losing') return `Ты проигрываешь ${h2h.you} - ${h2h.them}`;
  return `Счёт по матчам: ${h2h.you} - ${h2h.them}`;
}

function headToHeadClass(h2h) {
  if (!h2h || h2h.type === 'none') return 'text-slate-500';
  if (h2h.type === 'winning') return 'text-emerald-400';
  if (h2h.type === 'losing') return 'text-red-400/90';
  return 'text-slate-400';
}

function parseSavedState() {
  try {
    const raw = localStorage.getItem(SOCIAL_STATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return { ...INITIAL_STATE, ...parsed };
  } catch {
    return null;
  }
}

function avatarInitial(name) {
  return (name || 'G').trim().charAt(0).toUpperCase();
}

function isSelfFriendLike(candidate, user) {
  if (!candidate || !user) return false;
  const selfId = String(user.id || '');
  const candidateId = String(candidate.id || '');
  if (selfId && candidateId && selfId === candidateId) return true;

  const selfName = String(user.username || '').trim().toLowerCase();
  const candidateName = String(candidate.username || '').trim().toLowerCase();
  return !!selfName && !!candidateName && selfName === candidateName;
}

function activityLabelFromServerPresence(row) {
  if (!row || typeof row !== 'object') return 'Вне сети';
  const st = String(row.state || '').toLowerCase();
  const d = String(row.detail || '').toLowerCase();
  if (st === 'offline') return 'Вне сети';
  if (st === 'in_game') {
    if (d === 'seka') return 'В игре (Сека)';
    if (d === 'poker') return 'В игре (Покер)';
    if (d === 'belot') return 'В игре (Белот)';
    return 'В игре';
  }
  if (st === 'online') return 'В лобби';
  return 'Вне сети';
}

function cardPresenceFromServerAndLive(pid, uname, serverMap, liveById, liveByName) {
  const s = serverMap && typeof serverMap === 'object' ? serverMap[String(pid)] : null;
  const st = s && String(s.state || '').toLowerCase();
  if (st === 'online' || st === 'in_game' || st === 'offline') return st;
  return (
    liveById.get(String(pid)) ||
    liveByName.get(String(uname || '').trim().toLowerCase()) ||
    'offline'
  );
}

function FriendCard({
  friend,
  onSendGift,
  onChallenge,
  onRemove,
  onOpenProfile,
  hasOutgoingTableInvite,
  presence = 'offline',
}) {
  const presenceBadge =
    presence === 'in_game'
      ? { text: 'В игре', className: 'border-violet-400/45 bg-violet-500/20 text-violet-100' }
      : presence === 'online'
        ? { text: 'Онлайн', className: 'border-emerald-400/45 bg-emerald-500/20 text-emerald-100' }
        : { text: 'Оффлайн', className: 'border-slate-500/45 bg-slate-600/20 text-slate-300' };
  return (
    <article className="relative flex min-w-0 items-center gap-2 rounded-xl border border-slate-300/15 bg-gradient-to-b from-[#151f31] via-[#101726] to-[#090d15] p-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_8px_20px_rgba(0,0,0,0.38)]">
      <button
        type="button"
        onClick={() => onRemove(friend.id)}
        aria-label={`Убрать ${friend.username} из списка`}
        className="lobby-focus absolute right-1.5 top-1.5 z-[3] flex h-5 w-5 items-center justify-center rounded-md border border-white/10 bg-black/40 text-slate-400 transition hover:border-red-500/40 hover:bg-red-950/40 hover:text-red-200"
      >
        <X className="h-2.5 w-2.5" aria-hidden />
      </button>

      <div className="relative ml-0 mt-0 flex w-[66px] shrink-0 flex-col items-center">
        <div className="relative rounded-lg border-2 border-amber-500 bg-gradient-to-br from-amber-600/35 via-amber-800/45 to-amber-950/70 p-[2px] shadow-[0_0_14px_rgba(251,191,36,0.16)]">
          <button
            type="button"
            onClick={() => onOpenProfile(friend)}
            aria-label={`Открыть профиль ${friend.username}`}
            className="lobby-focus relative flex h-[52px] w-[52px] items-center justify-center overflow-hidden rounded-md bg-gradient-to-br from-[#1e3a5f] to-[#0f1c2e] text-base font-black text-white transition hover:brightness-110"
          >
            {avatarInitial(friend.username)}
          </button>
        </div>
        <div className="absolute -left-1 -top-1 z-[2] flex h-5 items-center gap-0.5 rounded-md border border-pink-400/45 bg-gradient-to-br from-pink-600 to-rose-950 px-1 shadow-md" aria-hidden>
          <span className="pr-0.5 text-[7px] font-black tabular-nums leading-none text-white">{friend.level}</span>
        </div>
      </div>

      <div className="min-w-0 flex-1 pr-14">
        <div className="mb-0.5 flex min-w-0 items-center gap-1.5">
          <p className="truncate text-[13px] font-bold text-white">{friend.username}</p>
          <span
            className={cn(
              'shrink-0 rounded-full border px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wide',
              presenceBadge.className
            )}
          >
            {presenceBadge.text}
          </span>
          {hasOutgoingTableInvite ? (
            <span className="shrink-0 rounded-full border border-amber-400/45 bg-amber-500/20 px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wide text-amber-100 shadow-[0_0_10px_rgba(251,191,36,0.35)]">
              Стол
            </span>
          ) : null}
          <span className="shrink-0 rounded-md border border-white/15 bg-white/[0.04] px-1 py-0.5 text-[9px] font-semibold uppercase text-slate-300">
            ур. {friend.level}
          </span>
        </div>
        <p className="truncate text-[10px] font-semibold text-amber-200/95">
          Выигрыш: {friend.winningsLabel} <LobbyFiskiIcon size="sm" className="inline opacity-90" />
        </p>
        <p className={cn('mt-0.5 truncate text-[10px] font-semibold', headToHeadClass(friend.headToHead))}>{headToHeadText(friend.headToHead)}</p>
        <p className="mt-0.5 truncate text-[10px] text-slate-400">{friend.lastPlayedLabel}</p>
      </div>

      <div className="absolute bottom-2 right-2 flex items-center gap-1.5">
        <button
          type="button"
          aria-label={`Подарок для ${friend.username}`}
          onClick={() => onSendGift(friend)}
          className="lobby-focus flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-amber-500/50 bg-gradient-to-b from-amber-600/90 to-amber-900 text-amber-50 shadow-[0_4px_12px_rgba(245,158,11,0.22)] transition hover:brightness-110"
        >
          <Gift className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
        </button>
        <button
          type="button"
          onClick={() => onChallenge(friend)}
          className="lobby-focus rounded-lg border border-emerald-500/45 bg-gradient-to-b from-emerald-600 to-emerald-900 px-2.5 py-1.5 text-center text-[9px] font-bold uppercase tracking-wide text-white shadow-[0_5px_14px_rgba(16,185,129,0.2)] transition hover:brightness-110"
        >
          Вызов
        </button>
      </div>
    </article>
  );
}

export function LobbyFriendsScreen({
  onBack,
  user,
  onToast,
  onAppendGlobalMessage,
  globalFriendRequests = [],
  onGlobalFriendRequestAction,
  outgoingTableInvites = [],
  incomingTableInvites = [],
  onMarkTableInviteRead,
  onRespondTableInvite,
  recommendedUsers = [],
  liveLobbyUsers = [],
  friendServerPresenceByUserId = {},
  onChallengeFriend,
  onSendFriendRequest,
  onOpenFriendDm,
  accessToken = '',
  /** Залогиненный аккаунт: одна правда с REST, без демо-слоя заявок. */
  useServerFriendRequests = false,
  /** Удаление принятой братвы из глобального стора (гость/демо). */
  onRemoveAcceptedFriend,
}) {
  const [activeTab, setActiveTab] = useState('friends');
  const [query, setQuery] = useState('');

  const saved = useMemo(() => parseSavedState(), []);
  const [incomingRequests] = useState(saved?.incomingRequests || INITIAL_STATE.incomingRequests);
  const [outgoingRequests, setOutgoingRequests] = useState(saved?.outgoingRequests || INITIAL_STATE.outgoingRequests);
  const [invites, setInvites] = useState(saved?.invites || INITIAL_STATE.invites);
  const [gifts, setGifts] = useState(saved?.gifts || INITIAL_STATE.gifts);
  const [coins, setCoins] = useState(saved?.coins ?? (Number(user?.balance) || INITIAL_STATE.coins));
  const [challengeTarget, setChallengeTarget] = useState(null);
  const [challengeSubmitting, setChallengeSubmitting] = useState(false);
  const [challengeStake, setChallengeStake] = useState(50);
  const [challengeAnte, setChallengeAnte] = useState(10);
  const [selectedFriendProfile, setSelectedFriendProfile] = useState(null);
  const uidStr = String(user?.id || '');

  const friends = useMemo(() => {
    const peers = acceptedFriendsFromRequests(globalFriendRequests, user).filter(
      (p) => p && !isSelfFriendLike(p, user)
    );
    const live = Array.isArray(liveLobbyUsers) ? liveLobbyUsers : [];
    const byId = new Map();
    const byName = new Map();
    live.forEach((u) => {
      const id = String(u?.id || '').trim();
      const nm = String(u?.username || '').trim().toLowerCase();
      if (id) byId.set(id, u);
      if (nm) byName.set(nm, u);
    });
    return peers.map((peer) => {
      const pid = String(peer.id || '').trim();
      const uname = String(peer.username || '').trim().toLowerCase();
      const liveRow = byId.get(pid) || (uname ? byName.get(uname) : null);
      const level = Math.max(Number(peer.level) || 1, Number(liveRow?.level) || 0);
      const id = liveRow?.id && String(liveRow.id).trim() ? String(liveRow.id).trim() : pid;
      const serverRow =
        friendServerPresenceByUserId && typeof friendServerPresenceByUserId === 'object'
          ? friendServerPresenceByUserId[pid]
          : null;
      const inGlobalChat =
        liveRow && String(liveRow.lastPlayedLabel || '').toLowerCase().includes('глобальном');
      let lastPlayedLabel;
      if (inGlobalChat) {
        lastPlayedLabel = liveRow.lastPlayedLabel;
      } else if (useServerFriendRequests && serverRow) {
        lastPlayedLabel = activityLabelFromServerPresence(serverRow);
      } else if (liveRow?.lastPlayedLabel) {
        lastPlayedLabel = liveRow.lastPlayedLabel;
      } else {
        lastPlayedLabel = 'Вне сети';
      }
      return {
        id,
        username: String(peer.username || 'Игрок').trim() || 'Игрок',
        level: level || 1,
        lastPlayedLabel,
        winningsLabel: '—',
        headToHead: { type: 'none' },
        avatarUrl: peer.avatarUrl || null,
      };
    });
  }, [globalFriendRequests, user, liveLobbyUsers, friendServerPresenceByUserId, useServerFriendRequests]);

  useEffect(() => {
    const payload = {
      incomingRequests,
      outgoingRequests,
      invites,
      gifts,
      coins,
    };
    try {
      localStorage.setItem(SOCIAL_STATE_KEY, JSON.stringify(payload));
    } catch {
      // ignore quota issues
    }
  }, [incomingRequests, outgoingRequests, invites, gifts, coins]);

  const toast = (msg) => {
    if (typeof onToast === 'function') onToast(msg);
  };

  const globalIncomingFriendReqs = useMemo(
    () => (Array.isArray(globalFriendRequests) ? globalFriendRequests.filter((r) => r.type === 'incoming') : []),
    [globalFriendRequests]
  );
  const incomingTotal =
    useServerFriendRequests && typeof onSendFriendRequest === 'function'
      ? globalIncomingFriendReqs.length
      : globalIncomingFriendReqs.length + incomingRequests.length;

  const myOutgoingGlobalFriendRequests = useMemo(() => {
    if (!uidStr || !Array.isArray(globalFriendRequests)) return [];
    return globalFriendRequests
      .filter((r) => r?.type === 'outgoing' && String(r.from?.id) === uidStr)
      .sort((a, b) => (Number(b?.createdAt) || 0) - (Number(a?.createdAt) || 0));
  }, [globalFriendRequests, uidStr]);

  const myIncomingGlobalFriendRequests = useMemo(() => {
    if (!uidStr || !Array.isArray(globalFriendRequests)) return [];
    return globalFriendRequests
      .filter((r) => r?.type === 'incoming' && String(r.to?.id) === uidStr)
      .sort((a, b) => (Number(b?.createdAt) || 0) - (Number(a?.createdAt) || 0));
  }, [globalFriendRequests, uidStr]);

  const globalOutgoingPendingToIds = useMemo(() => {
    if (!uidStr || !Array.isArray(globalFriendRequests)) return new Set();
    return new Set(
      globalFriendRequests
        .filter(
          (r) =>
            r?.status === 'pending' &&
            r?.type === 'outgoing' &&
            String(r.from?.id || '') === uidStr
        )
        .map((r) => String(r.to?.id || ''))
        .filter(Boolean)
    );
  }, [globalFriendRequests, uidStr]);

  const activityPendingGlobalFrCount = useMemo(() => {
    if (!Array.isArray(globalFriendRequests) || !uidStr) return 0;
    return globalFriendRequests.filter(
      (r) =>
        r?.status === 'pending' &&
        ((r.type === 'outgoing' && String(r.from?.id) === uidStr) ||
          (r.type === 'incoming' && String(r.to?.id) === uidStr))
    ).length;
  }, [globalFriendRequests, uidStr]);

  const formatFrTime = (ts) => {
    const t = Number(ts) || Date.now();
    try {
      return new Date(t).toLocaleString('ru-RU', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '';
    }
  };

  const friendRequestStatusLabel = (status) => {
    if (status === 'accepted') return { text: 'Принято', className: 'bg-emerald-500/20 text-emerald-200' };
    if (status === 'declined') return { text: 'Отклонено', className: 'bg-slate-600/40 text-slate-300' };
    return { text: 'Ожидание', className: 'bg-amber-500/20 text-amber-200' };
  };

  const incomingTableForMe = useMemo(() => {
    const uid = String(user?.id || '');
    const uname = String(user?.username || '').trim();
    return (incomingTableInvites || []).filter(
      (i) =>
        i &&
        !i.read &&
        (String(i.toUserId || '') === uid || (i.toUsername && String(i.toUsername).trim() === uname))
    );
  }, [incomingTableInvites, user?.id, user?.username]);

  const outgoingInviteFriendIds = useMemo(() => {
    const s = new Set();
    (outgoingTableInvites || []).forEach((o) => {
      if (o?.friendId) s.add(String(o.friendId));
    });
    return s;
  }, [outgoingTableInvites]);
  const livePresenceById = useMemo(() => {
    const m = new Map();
    (Array.isArray(liveLobbyUsers) ? liveLobbyUsers : []).forEach((u) => {
      const uid = String(u?.id || '').trim();
      if (!uid) return;
      const last = String(u?.lastPlayedLabel || '').toLowerCase();
      const presence = last.includes('игр') ? 'in_game' : 'online';
      m.set(uid, presence);
    });
    return m;
  }, [liveLobbyUsers]);
  const livePresenceByName = useMemo(() => {
    const m = new Map();
    (Array.isArray(liveLobbyUsers) ? liveLobbyUsers : []).forEach((u) => {
      const uname = String(u?.username || '').trim().toLowerCase();
      if (!uname) return;
      const last = String(u?.lastPlayedLabel || '').toLowerCase();
      const presence = last.includes('игр') ? 'in_game' : 'online';
      m.set(uname, presence);
    });
    return m;
  }, [liveLobbyUsers]);

  const friendIds = useMemo(() => new Set(friends.map((f) => String(f.id || ''))), [friends]);
  const outgoingToIds = useMemo(() => {
    const fromLocal =
      useServerFriendRequests && typeof onSendFriendRequest === 'function'
        ? []
        : outgoingRequests.map((r) => String(r.to?.id || ''));
    return new Set([...fromLocal, ...globalOutgoingPendingToIds]);
  }, [outgoingRequests, globalOutgoingPendingToIds, useServerFriendRequests, onSendFriendRequest]);

  const recommended = useMemo(
    () =>
      (Array.isArray(recommendedUsers) ? recommendedUsers : [])
        .filter((p) => p && (p.id != null || p.username))
        .map((p) => ({
          id: String(p.id ?? '').trim(),
          username: String(p.username || '').trim() || 'Игрок',
          level: Number(p.level) || 1,
          lastPlayedLabel: String(p.lastPlayedLabel || 'Игрок в лобби'),
          winningsLabel: String(p.winningsLabel || '—'),
          headToHead: p.headToHead || { type: 'none' },
        }))
        .filter((p) => p.id && !isSelfFriendLike(p, user))
        .filter((p) => !friendIds.has(String(p.id)) && !outgoingToIds.has(String(p.id))),
    [recommendedUsers, user, friendIds, outgoingToIds]
  );

  const filteredFriends = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return friends;
    return friends.filter((f) => f.username.toLowerCase().includes(q));
  }, [friends, query]);

  const filteredRecommended = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return recommended;
    return recommended.filter((f) => f.username.toLowerCase().includes(q));
  }, [recommended, query]);

  const removeFriend = (friendId) => {
    if (typeof onRemoveAcceptedFriend === 'function') {
      onRemoveAcceptedFriend(friendId);
      toast('Убрано из списка братвы');
      return;
    }
    toast('Удаление недоступно');
  };

  const sendFriendRequest = (candidate) => {
    if (isSelfFriendLike(candidate, user)) {
      toast('Нельзя отправить заявку самому себе');
      return;
    }
    if (friendIds.has(String(candidate.id || ''))) {
      toast('Этот кореш уже в братве');
      return;
    }
    const cid = String(candidate.id || '');
    if (outgoingToIds.has(cid)) {
      toast('Заявка уже отправлена');
      return;
    }

    if (typeof onSendFriendRequest === 'function') {
      onSendFriendRequest({
        id: candidate.id,
        username: candidate.username,
        clan: candidate.clan || 'Без клана',
        level: Number(candidate.level) || 1,
        avatarUrl: candidate.avatarUrl || null,
      });
      return;
    }

    const req = { id: uid('fr-out'), to: candidate, createdAt: Date.now() };
    setOutgoingRequests((prev) => [req, ...prev]);
    toast(`Заявка отправлена (демо): ${candidate.username}`);

    setTimeout(() => {
      setOutgoingRequests((prevOut) => {
        const stillPending = prevOut.some((x) => x.id === req.id);
        if (!stillPending) return prevOut;
        if (Math.random() < 0.55) {
          toast(`${candidate.username} принял(а) заявку (демо; список — из глобального стора)`);
          return prevOut.filter((x) => x.id !== req.id);
        }
        return prevOut;
      });
    }, 2200);
  };

  const addByUniqueId = () => {
    const value = typeof window !== 'undefined' ? window.prompt('Уникальный ID или ник', '') : '';
    const q = String(value || '').trim().toLowerCase();
    if (!q) return;
    const found = PEOPLE_POOL.find((p) => p.id.toLowerCase() === q || p.username.toLowerCase() === q);
    if (!found) {
      toast('Пользователь не найден в демо-пуле. В онлайне используйте ник из чата или рекомендаций.');
      return;
    }
    sendFriendRequest(found);
  };

  const sendGift = (friend) => {
    if (coins < GIFT_AMOUNT) {
      toast('Недостаточно фишек для подарка');
      return;
    }
    setCoins((prev) => prev - GIFT_AMOUNT);
    const record = { id: uid('gift'), to: friend, amount: GIFT_AMOUNT, createdAt: Date.now() };
    setGifts((prev) => [record, ...prev].slice(0, 20));
    toast(`Подарок ${GIFT_AMOUNT} фишек отправлен: ${friend.username}`);
  };

  const challengeFriend = (friend) => {
    if (!friend || !friend.id) return;
    setChallengeStake(50);
    setChallengeAnte(10);
    setChallengeTarget(friend);
  };

  const openFriendProfile = (friend) => {
    if (!friend || !friend.id) return;
    const level = Math.max(1, Number(friend.level) || 1);
    const h2h = friend?.headToHead || { type: 'none' };
    const totalMatches =
      Number.isFinite(Number(h2h?.you)) && Number.isFinite(Number(h2h?.them))
        ? Math.max(0, Number(h2h.you)) + Math.max(0, Number(h2h.them))
        : 0;
    const winRate =
      totalMatches > 0 && Number.isFinite(Number(h2h?.you))
        ? `${Math.round((Math.max(0, Number(h2h.you)) / totalMatches) * 100)}%`
        : 'Нет данных';
    const rankTitle = level >= 70 ? 'Легенда лобби' : level >= 45 ? 'Ветеран стола' : level >= 20 ? 'Опытный игрок' : 'Новичок';
    setSelectedFriendProfile({
      id: String(friend.id),
      username: String(friend.username || 'Игрок').trim() || 'Игрок',
      avatarUrl: friend?.avatarUrl || '',
      level,
      rankTitle,
      rewardsLabel: level >= 60 ? 'Элитные награды сезона' : 'Стандартные награды сезона',
      stats: [
        { label: 'Всего дуэлей', value: totalMatches ? String(totalMatches) : 'Нет данных' },
        { label: 'Процент побед', value: winRate },
        { label: 'Выигрыш', value: friend?.winningsLabel || 'Нет данных' },
        { label: 'Последняя активность', value: friend?.lastPlayedLabel || 'Нет данных' },
      ],
    });
  };

  const closeFriendProfile = () => setSelectedFriendProfile(null);
  const challengeFromProfile = (profileFriend) => {
    if (!profileFriend?.id) return;
    challengeFriend({
      id: String(profileFriend.id),
      username: String(profileFriend.username || selectedFriendProfile?.username || 'Игрок'),
      level: Number(profileFriend.level || selectedFriendProfile?.level) || 1,
      avatarUrl: profileFriend.avatarUrl || selectedFriendProfile?.avatarUrl || '',
    });
  };
  const openDmFromProfile = (profileFriend) => {
    if (!profileFriend?.id || typeof onOpenFriendDm !== 'function') return;
    onOpenFriendDm({
      id: String(profileFriend.id),
      username: String(profileFriend.username || selectedFriendProfile?.username || 'Игрок'),
      level: Number(profileFriend.level || selectedFriendProfile?.level) || 1,
      avatarUrl: profileFriend.avatarUrl || selectedFriendProfile?.avatarUrl || '',
    });
  };

  const closeChallengePicker = () => {
    if (challengeSubmitting) return;
    setChallengeTarget(null);
  };

  const sendChallengeByMode = async (mode) => {
    if (!challengeTarget || challengeSubmitting) return;
    const modeId = String(mode || '').trim().toLowerCase();
    if (!modeId) return;
    setChallengeSubmitting(true);
    try {
      if (typeof onChallengeFriend === 'function') {
        await onChallengeFriend({
          friend: challengeTarget,
          mode: modeId,
          stake: challengeStake,
          ante: modeId === 'seka' ? challengeAnte : 0,
        });
      } else {
        const invite = {
          id: uid('inv'),
          kind: 'outgoing',
          friend: challengeTarget,
          status: 'sent',
          mode: modeId,
          createdAt: Date.now(),
        };
        setInvites((prev) => [invite, ...prev]);
        if (typeof onAppendGlobalMessage === 'function') {
          onAppendGlobalMessage('Система', `Вы отправили вызов игроку ${challengeTarget.username}`);
        }
        toast(`Вызов отправлен: ${challengeTarget.username}`);
      }
      setChallengeTarget(null);
    } catch (err) {
      toast(String(err?.message || 'Не удалось отправить вызов'));
    } finally {
      setChallengeSubmitting(false);
    }
  };

  const isRequestPending = (candidateId) => outgoingToIds.has(String(candidateId || ''));
  const canActOnIncomingRequest = (request) =>
    request?.status === 'pending' &&
    request?.type === 'incoming' &&
    uidStr &&
    String(request?.to?.id || '') === uidStr &&
    (!useServerFriendRequests || String(request?.id || '').startsWith('fr-srv-'));

  const handleIncomingFriendRequestDecision = (request, nextStatus) => {
    if (!request?.id || !canActOnIncomingRequest(request)) return;
    onGlobalFriendRequestAction?.(request.id, nextStatus, request.from?.id);
  };

  return (
    <div className="fixed inset-0 z-[85] flex flex-col bg-[#05080e] text-foreground" role="dialog" aria-modal="true" aria-labelledby="friends-screen-title">
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a1018] via-[#05080e] to-[#030508]" />
        <div className="absolute left-1/4 top-0 h-40 w-40 -translate-x-1/2 rounded-full bg-violet-500/6 blur-3xl" />
        <div className="absolute bottom-1/3 right-0 h-56 w-56 rounded-full bg-amber-500/6 blur-3xl" />
      </div>

      <h1 id="friends-screen-title" className="sr-only">Моя братва</h1>

      <header className="relative z-[1] shrink-0 border-b border-white/[0.06] bg-black/30 px-2 py-1.5 pt-[max(0.45rem,env(safe-area-inset-top,0px))] backdrop-blur-md sm:px-3">
        <div className="mx-auto flex max-w-6xl flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-2">
          <div className="flex items-center gap-2">
            <button type="button" onClick={onBack} aria-label="Назад" className="lobby-focus flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-red-500/55 bg-gradient-to-b from-red-950/80 to-red-950/50 text-red-100 shadow-[0_4px_16px_rgba(239,68,68,0.2)] transition hover:border-red-400/70 hover:from-red-900/85">
              <ArrowLeft className="h-5 w-5" aria-hidden />
            </button>
            <div className="relative min-w-0 flex-1 sm:max-w-[220px]">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" aria-hidden />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Поиск кореша..."
                aria-label="Поиск кореша"
                className="lobby-focus w-full rounded-xl border border-white/[0.08] bg-black/35 py-2 pl-9 pr-3 text-sm text-slate-100 placeholder:text-slate-500 outline-none ring-0 focus:border-amber-500/35 focus:bg-black/45"
              />
            </div>
          </div>

          <div className="flex items-stretch gap-1 rounded-xl border border-slate-200/10 bg-gradient-to-b from-[#182235]/90 to-[#0c121d]/95 p-1 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] sm:ml-auto" role="tablist" aria-label="Разделы братвы">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'lobby-focus min-h-[36px] min-w-0 rounded-lg border px-2 py-1 text-center text-[10px] font-bold uppercase leading-tight tracking-wide transition-all duration-200 sm:min-w-[112px] sm:text-[11px]',
                  activeTab === tab.id
                    ? 'border-amber-400/35 bg-gradient-to-b from-amber-300/30 to-amber-700/20 text-amber-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.22),0_8px_18px_rgba(245,158,11,0.2)]'
                    : 'border-transparent bg-transparent text-slate-400 hover:border-white/10 hover:bg-white/[0.06] hover:text-slate-200'
                )}
              >
                <span className="inline-flex items-center gap-1">
                  {tab.label}
                  {tab.id === 'friends' ? (
                    <LobbyHubNotificationIndicator
                      count={incomingTableForMe.length}
                      layout="inlineTab"
                    />
                  ) : null}
                  {tab.id === 'incoming' ? (
                    <LobbyHubNotificationIndicator
                      count={incomingTotal}
                      layout="inlineTab"
                    />
                  ) : null}
                  {tab.id === 'activity' ? (
                    <LobbyHubNotificationIndicator count={activityPendingGlobalFrCount} layout="inlineTab" />
                  ) : null}
                </span>
              </button>
            ))}
          </div>
        </div>
      </header>

      <div className="relative z-[1] flex min-h-0 flex-1 flex-col overflow-hidden">
        {activeTab === 'friends' ? (
          <>
            <div className="lobby-mp-scroll min-h-0 flex-1 space-y-2 overflow-y-auto px-3 py-2.5 sm:px-4">
              {incomingTableForMe.length ? (
                <section className="rounded-xl border border-cyan-400/35 bg-gradient-to-br from-cyan-950/50 to-[#0a111d]/95 p-2.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
                  <p className="mb-2 text-[10px] font-black uppercase tracking-[0.14em] text-cyan-200/90">
                    Приглашение за стол
                  </p>
                  <ul className="space-y-2">
                    {incomingTableForMe.map((inv) => (
                      <li
                        key={inv.id}
                        className="flex flex-col gap-2 rounded-lg border border-cyan-500/25 bg-black/35 px-2.5 py-2 sm:flex-row sm:items-center sm:justify-between"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-xs font-bold text-white">
                            {inv.fromUsername || 'Игрок'} зовёт на матч
                            {inv.roomCode ? (
                              <>
                                {' '}
                                в комнату <span className="font-mono text-cyan-200">{inv.roomCode}</span>
                              </>
                            ) : null}
                          </p>
                          <p className="mt-0.5 text-[10px] text-slate-400">
                            Режим: {inv.mode || 'seka'}
                          </p>
                        </div>
                        <div className="flex shrink-0 items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => {
                              if (typeof onRespondTableInvite === 'function') {
                                onRespondTableInvite(inv, 'accept-now');
                              } else if (typeof onMarkTableInviteRead === 'function') {
                                onMarkTableInviteRead(inv.id);
                              }
                            }}
                            className="lobby-focus rounded-lg border border-emerald-500/45 bg-emerald-900/40 px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wide text-emerald-100 transition hover:bg-emerald-800/55"
                          >
                            Принять
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              if (typeof onRespondTableInvite === 'function') {
                                onRespondTableInvite(inv, 'declined');
                              } else if (typeof onMarkTableInviteRead === 'function') {
                                onMarkTableInviteRead(inv.id);
                              }
                            }}
                            className="lobby-focus rounded-lg border border-red-500/40 bg-red-900/35 px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wide text-red-100 transition hover:bg-red-800/45"
                          >
                            Отклонить
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}

              <section className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
                {filteredFriends.map((friend) => (
                  <FriendCard
                    key={friend.id}
                    friend={friend}
                    onSendGift={sendGift}
                    onChallenge={challengeFriend}
                    onRemove={removeFriend}
                    onOpenProfile={openFriendProfile}
                    hasOutgoingTableInvite={outgoingInviteFriendIds.has(String(friend.id))}
                    presence={cardPresenceFromServerAndLive(
                      String(friend.id),
                      friend.username,
                      friendServerPresenceByUserId,
                      livePresenceById,
                      livePresenceByName
                    )}
                  />
                ))}
                {!filteredFriends.length ? (
                  <div className="col-span-full rounded-xl border border-white/10 bg-black/25 px-3 py-4 text-center text-sm text-slate-400">
                    Поиск не дал результатов.
                  </div>
                ) : null}
              </section>
            </div>
          </>
        ) : null}

        {activeTab === 'incoming' ? (
          <div className="lobby-mp-scroll flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-3 py-2.5 sm:px-4">
            {myIncomingGlobalFriendRequests.length ? (
              <section className="rounded-xl border border-sky-500/25 bg-sky-950/20 p-2.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
                <p className="mb-2 text-[10px] font-black uppercase tracking-[0.12em] text-sky-200/95">
                  Входящие запросы закорефаниться
                </p>
                <ul className="space-y-1.5">
                  {myIncomingGlobalFriendRequests.map((r) => {
                    const st = friendRequestStatusLabel(r.status);
                    const pending = r.status === 'pending';
                    return (
                      <li
                        key={r.id}
                        className="flex items-start justify-between gap-2 rounded-md border border-white/10 bg-black/35 px-2 py-1.5"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-[11px] text-slate-200">
                            <span className="font-semibold text-white">{r.from?.username || 'Игрок'}</span>
                            <span className="text-slate-500"> · </span>
                            <span className="text-slate-400">ур. {Number(r.from?.level) || 1}</span>
                          </p>
                          <p className="mt-0.5 text-[10px] text-slate-500">{formatFrTime(r.createdAt)}</p>
                        </div>
                        {pending ? (
                          <div className="flex shrink-0 justify-end gap-1">
                            <button
                              type="button"
                              onClick={() => handleIncomingFriendRequestDecision(r, 'accepted')}
                              className="lobby-focus rounded border border-emerald-500/40 bg-emerald-900/35 px-1.5 py-0.5 text-[10px] text-emerald-200"
                            >
                              Принять
                            </button>
                            <button
                              type="button"
                              onClick={() => handleIncomingFriendRequestDecision(r, 'declined')}
                              className="lobby-focus rounded border border-red-500/35 bg-red-900/35 px-1.5 py-0.5 text-[10px] text-red-200"
                            >
                              Отклонить
                            </button>
                          </div>
                        ) : (
                          <span
                            className={cn(
                              'shrink-0 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide',
                              st.className
                            )}
                          >
                            {st.text}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </section>
            ) : (
              <div className="rounded-xl border border-white/10 bg-black/25 px-3 py-4 text-center text-sm text-slate-400">
                Входящих запросов пока нет.
              </div>
            )}
          </div>
        ) : null}

        {activeTab === 'recommended' ? (
          <div className="lobby-mp-scroll flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto px-3 py-2.5 sm:px-4">
            <div className="rounded-xl border border-white/10 bg-black/25 p-2 text-[11px] text-slate-400">
              Рекомендации формируются по реальным пользователям лобби.
            </div>
            {filteredRecommended.length ? filteredRecommended.map((candidate) => (
              <div key={candidate.id} className="flex items-center justify-between gap-2 rounded-xl border border-white/10 bg-[#111826]/80 px-2.5 py-2">
                <div className="min-w-0">
                  <p className="truncate text-[13px] font-bold text-white">{candidate.username}</p>
                  <p className="text-[10px] text-slate-500">Уровень {candidate.level} · {candidate.lastPlayedLabel}</p>
                </div>
                <button
                  type="button"
                  disabled={isRequestPending(candidate.id)}
                  onClick={() => sendFriendRequest(candidate)}
                  className="lobby-focus inline-flex items-center gap-1 rounded-lg border border-cyan-500/40 bg-cyan-900/35 px-2 py-1.5 text-[11px] font-semibold text-cyan-200 transition hover:bg-cyan-800/40 disabled:opacity-45"
                >
                  <UserPlus className="h-3.5 w-3.5" aria-hidden />
                  {isRequestPending(candidate.id) ? 'Отправлено' : 'Добавить'}
                </button>
              </div>
            )) : <p className="text-center text-sm text-slate-500">Подходящих игроков не найдено.</p>}
          </div>
        ) : null}

        {activeTab === 'activity' ? (
          <div className="grid min-h-0 flex-1 grid-cols-1 gap-2 overflow-hidden p-2.5 sm:p-3">
            <section className="flex min-h-0 flex-col rounded-xl border border-white/10 bg-black/25 p-2">
              <div className="mb-2 flex items-center justify-between gap-2">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-cyan-200">Активность и заявки</h3>
              </div>

              {myOutgoingGlobalFriendRequests.length || myIncomingGlobalFriendRequests.length ? (
                <div className="mb-2 space-y-2">
                  {myOutgoingGlobalFriendRequests.length ? (
                    <div className="rounded-lg border border-emerald-500/25 bg-emerald-950/20 p-2">
                      <p className="mb-1.5 text-[10px] font-black uppercase tracking-[0.12em] text-emerald-200/95">
                        Запросы закорефаниться · от вас
                      </p>
                      <ul className="space-y-1.5">
                        {myOutgoingGlobalFriendRequests.map((r) => {
                          const st = friendRequestStatusLabel(r.status);
                          return (
                            <li
                              key={r.id}
                              className="flex flex-col gap-1.5 rounded-md border border-white/10 bg-black/35 px-2 py-1.5 sm:flex-row sm:items-start sm:justify-between"
                            >
                              <div className="min-w-0">
                                <p className="truncate text-[11px] text-slate-200">
                                  <UserPlus className="mr-1 inline h-3.5 w-3.5 shrink-0 text-emerald-300" aria-hidden />
                                  <span className="font-semibold text-white">{r.to?.username || 'Игрок'}</span>
                                  <span className="text-slate-500"> · </span>
                                  <span className="text-slate-400">ур. {Number(r.to?.level) || 1}</span>
                                </p>
                                <p className="mt-0.5 text-[10px] text-slate-500">{formatFrTime(r.createdAt)}</p>
                              </div>
                              <span
                                className={cn(
                                  'shrink-0 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide',
                                  st.className
                                )}
                              >
                                {st.text}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ) : null}
                  {myIncomingGlobalFriendRequests.length ? (
                    <div className="rounded-lg border border-sky-500/25 bg-sky-950/20 p-2">
                      <p className="mb-1.5 text-[10px] font-black uppercase tracking-[0.12em] text-sky-200/95">
                        Запросы закорефаниться · к вам
                      </p>
                      <ul className="space-y-1.5">
                        {myIncomingGlobalFriendRequests.map((r) => {
                          const st = friendRequestStatusLabel(r.status);
                          return (
                            <li
                              key={r.id}
                              className="flex flex-col gap-1.5 rounded-md border border-white/10 bg-black/35 px-2 py-1.5 sm:flex-row sm:items-start sm:justify-between"
                            >
                              <div className="min-w-0">
                                <p className="truncate text-[11px] text-slate-200">
                                  <UserPlus className="mr-1 inline h-3.5 w-3.5 shrink-0 text-sky-300" aria-hidden />
                                  От <span className="font-semibold text-white">{r.from?.username || 'Игрок'}</span>
                                  <span className="text-slate-500"> · </span>
                                  <span className="text-slate-400">ур. {Number(r.from?.level) || 1}</span>
                                </p>
                                <p className="mt-0.5 text-[10px] text-slate-500">{formatFrTime(r.createdAt)}</p>
                              </div>
                              <span
                                className={cn(
                                  'shrink-0 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide',
                                  st.className
                                )}
                              >
                                {st.text}
                              </span>
                              {canActOnIncomingRequest(r) ? (
                                <div className="flex shrink-0 justify-end gap-1">
                                  <button
                                    type="button"
                                    onClick={() => handleIncomingFriendRequestDecision(r, 'accepted')}
                                    className="lobby-focus rounded border border-emerald-500/40 bg-emerald-900/35 px-1.5 py-0.5 text-[10px] text-emerald-200"
                                  >
                                    Принять
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleIncomingFriendRequestDecision(r, 'declined')}
                                    className="lobby-focus rounded border border-red-500/35 bg-red-900/35 px-1.5 py-0.5 text-[10px] text-red-200"
                                  >
                                    Отклонить
                                  </button>
                                </div>
                              ) : null}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {outgoingRequests.length &&
              !(useServerFriendRequests && typeof onSendFriendRequest === 'function') ? (
                <div className="mb-2 rounded-lg border border-white/10 bg-black/25 p-2">
                  <p className="mb-1.5 text-[10px] font-black uppercase tracking-[0.12em] text-slate-400">
                    Локальные заявки (демо)
                  </p>
                  <ul className="space-y-1">
                    {outgoingRequests.map((r) => (
                      <li key={r.id} className="truncate text-[11px] text-slate-300">
                        <UserPlus className="mr-1 inline h-3 w-3 text-slate-500" aria-hidden />
                        Ожидаем ответа: <span className="font-semibold text-slate-100">{r.to?.username}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              <div className="rounded-lg border border-violet-500/20 bg-violet-950/15 px-3 py-2.5 text-[11px] leading-relaxed text-violet-100/90">
                Личка с корешами — в главном лобби: откройте чат и вкладку «Малявы».
              </div>
            </section>
          </div>
        ) : null}
      </div>

      <footer className="relative z-[1] shrink-0 border-t border-white/[0.06] bg-black/35 px-3 py-2.5 pb-[max(0.75rem,env(safe-area-inset-bottom,0px))] backdrop-blur-md sm:px-4">
        <p className="mx-auto mb-2 max-w-3xl text-center text-[10px] leading-snug text-slate-400 sm:text-[11px]">
          Зови братву в Сека и поднимай награды! Баланс для подгонов: <span className="font-semibold text-amber-300">{coins}</span> фишек
        </p>
        <div className="mx-auto flex max-w-lg flex-col gap-1.5 sm:flex-row sm:justify-center">
          <button type="button" onClick={() => toast('Подтянуть братву — скоро (шаринг)')} className="lobby-focus w-full rounded-xl border border-sky-500/45 bg-gradient-to-b from-sky-600 to-sky-900 py-2.5 text-[13px] font-bold text-white shadow-[0_6px_20px_rgba(14,165,233,0.2)] transition hover:brightness-110 sm:w-auto sm:min-w-[170px] sm:px-5">Подтянуть братву</button>
          <button type="button" onClick={addByUniqueId} className="lobby-focus w-full rounded-xl border border-orange-500/45 bg-gradient-to-b from-orange-600 to-orange-900 py-2.5 text-[13px] font-bold text-white shadow-[0_6px_20px_rgba(234,88,12,0.2)] transition hover:brightness-110 sm:w-auto sm:min-w-[190px] sm:px-5">Добавить по ID</button>
        </div>
      </footer>
      {challengeTarget ? (
        <div className="fixed inset-0 z-[95] flex items-end justify-center bg-black/65 p-3 sm:items-center">
          <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#0d1524]/95 p-3 shadow-[0_18px_40px_rgba(0,0,0,0.45)] backdrop-blur-md">
            <p className="text-xs font-black uppercase tracking-[0.12em] text-cyan-200/90">Вызов на матч</p>
            <p className="mt-1 text-sm text-white">
              Куда отправить вызов для <span className="font-bold">{challengeTarget.username}</span>?
            </p>
            <div className="mt-3 rounded-xl border border-white/10 bg-black/20 p-2.5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-300">Ставка</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {CHALLENGE_STAKE_OPTIONS.map((val) => (
                  <button
                    key={val}
                    type="button"
                    disabled={challengeSubmitting}
                    onClick={() => setChallengeStake(val)}
                    className={cn(
                      'lobby-focus rounded-lg border px-2.5 py-1 text-xs font-semibold transition',
                      challengeStake === val
                        ? 'border-cyan-300/70 bg-cyan-500/20 text-cyan-100'
                        : 'border-white/12 bg-white/[0.04] text-slate-200 hover:bg-white/[0.08]'
                    )}
                  >
                    {val}
                  </button>
                ))}
              </div>
              <div className="mt-2 flex items-center gap-2">
                <span className="text-[11px] text-slate-300">Анте</span>
                <input
                  type="number"
                  min={1}
                  max={challengeStake}
                  value={challengeAnte}
                  disabled={challengeSubmitting}
                  onChange={(e) => {
                    const next = Math.max(1, Math.min(challengeStake, Math.floor(Number(e.target.value) || 1)));
                    setChallengeAnte(next);
                  }}
                  className="w-20 rounded-md border border-white/15 bg-black/35 px-2 py-1 text-xs text-white outline-none"
                />
              </div>
            </div>
            <div className="mt-3 grid grid-cols-1 gap-2">
              {CHALLENGE_MODE_OPTIONS.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  disabled={challengeSubmitting}
                  onClick={() => sendChallengeByMode(opt.id)}
                  className="lobby-focus rounded-xl border border-white/12 bg-white/[0.04] px-3 py-2 text-left transition hover:bg-white/[0.08] disabled:opacity-50"
                >
                  <p className="text-sm font-bold text-white">{opt.label}</p>
                  <p className="text-[11px] text-slate-400">{opt.sub}</p>
                </button>
              ))}
            </div>
            <div className="mt-3 flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={closeChallengePicker}
                disabled={challengeSubmitting}
                className="lobby-focus rounded-lg border border-white/12 bg-white/[0.03] px-3 py-2 text-xs font-semibold text-slate-200 transition hover:bg-white/[0.07] disabled:opacity-50"
              >
                Отмена
              </button>
              {challengeSubmitting ? (
                <span className="inline-flex items-center gap-1.5 text-xs text-cyan-200">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                  Отправка...
                </span>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
      <TableSeatProfileModal
        open={selectedFriendProfile != null}
        onClose={closeFriendProfile}
        variant="human"
        displayName={selectedFriendProfile?.username || ''}
        userId={selectedFriendProfile?.id || ''}
        initialAvatarUrl={selectedFriendProfile?.avatarUrl || ''}
        currentUserId={String(user?.id || '')}
        accessToken={accessToken}
        onSendFriendRequest={onSendFriendRequest}
        actionsMode="friend"
        onChallengeAction={challengeFromProfile}
        onMessageAction={openDmFromProfile}
        extendedProfile={
          selectedFriendProfile
            ? {
                rankTitle: selectedFriendProfile.rankTitle,
                rewardsLabel: selectedFriendProfile.rewardsLabel,
                stats: selectedFriendProfile.stats,
              }
            : null
        }
      />
    </div>
  );
}
